import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, HeartPulse, Pill, FlaskConical, Stethoscope, Activity, ShieldCheck, Sparkles, Crown } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { BookingForm } from "@/components/BookingForm";
import heroAsset from "@/assets/healic-membership-card.jpg.asset.json";
import membershipMascot from "@/assets/healic-mascot-membership.png.asset.json";
const heroImg = heroAsset.url;

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/healic-plus")({
  head: () => ({
    meta: [
      { title: "Healic+ Membership Card — Huge Savings on Medical Bills" },
      { name: "description", content: "Healic+ Membership Card — choose Heal, Heal+ or Heal++ and save on medicines, diagnostics, full body checkups, GP consultations and more for you and your family." },
      { property: "og:title", content: "Healic+ Membership Card — Healic Multispeciality Hospital" },
      { property: "og:description", content: "Exclusive benefits: huge savings on medicines, diagnostics, checkups & GP consultations across Healic Hospitals." },
      { property: "og:image", content: `${SITE_URL}/_l/healic-plus-card.jpg` },
      { property: "og:url", content: `${SITE_URL}/healic-plus` },
      { property: "og:type", content: "website" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/healic-plus` }],
  }),
  component: HealicPlusPage,
});

type Pkg = {
  id: string;
  name: string;
  tagline: string;
  pay: string;
  get: string;
  instant: string;
  icon: typeof HeartPulse;
  highlight?: boolean;
  benefits: { label: string; value: string }[];
};

const packages: Pkg[] = [
  {
    id: "heal",
    name: "Heal",
    tagline: "Essential family cover",
    pay: "₹1,800",
    get: "₹2,000",
    instant: "₹200 • 11% bonus",
    icon: HeartPulse,
    benefits: [
      { label: "Medicines & Healic Products", value: "Up to 15% Off" },
      { label: "Diagnostics & Path Lab", value: "Up to 20% Off" },
      { label: "Full Body Checkups", value: "2 Checkups · 1 Free + 1 at 50% Off (67 parameters · Worth ₹1,500)" },
      { label: "GP Consultation", value: "Unlimited Free — Worth ₹7,200" },
      { label: "Regular Health Checkups", value: "Free ECG, RBS, BP — once a month at all Healic Hospitals" },
    ],
  },
  {
    id: "heal-plus",
    name: "Heal +",
    tagline: "Most popular",
    pay: "₹4,350",
    get: "₹5,000",
    instant: "₹650 • 15% bonus",
    icon: Sparkles,
    highlight: true,
    benefits: [
      { label: "Medicines & Healic Products", value: "Up to 15% Off" },
      { label: "Diagnostics & Path Lab", value: "Up to 20% Off (not applicable for packages)" },
      { label: "Full Body Checkups", value: "2 Checkups · 1 Free + 1 at 50% Off (83 parameters · Worth ₹4,600)" },
      { label: "GP Consultation", value: "Unlimited Free — Worth ₹7,200" },
      { label: "Regular Health Checkups", value: "Free ECG, RBS, BP — once a month at all Healic Hospitals" },
    ],
  },
  {
    id: "heal-plus-plus",
    name: "Heal ++",
    tagline: "Premium care for the whole parivar",
    pay: "₹8,500",
    get: "₹10,000",
    instant: "₹1,500 • 17% bonus",
    icon: Crown,
    benefits: [
      { label: "Medicines & Healic Products", value: "Up to 15% Off" },
      { label: "Diagnostics & Path Lab", value: "Up to 20% Off" },
      { label: "Full Body Checkups", value: "Free 2 Full Body Checkups (87 parameters · Worth ₹6,600)" },
      { label: "GP Consultation", value: "Unlimited Free — Worth ₹7,200" },
      { label: "Regular Health Checkups", value: "Free ECG, RBS, BP — once a month at all Healic Hospitals" },
    ],
  },
];

const perks = [
  { icon: Pill, title: "Medicines & Healic Products", desc: "Discounts every time you refill at Healic Pharmacy." },
  { icon: FlaskConical, title: "Diagnostics & Path Lab", desc: "Save on lab tests, imaging and routine diagnostics." },
  { icon: Activity, title: "Full Body Checkups", desc: "Comprehensive parameter packages worth thousands." },
  { icon: Stethoscope, title: "GP Consultation", desc: "Unlimited free general physician visits all year." },
  { icon: ShieldCheck, title: "Monthly Vitals", desc: "Free ECG, RBS & BP — once every month at any Healic." },
];

function HealicPlusPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 py-4 lg:px-8 lg:py-6">
        <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary-soft via-background to-muted shadow-card">
          <div className="grid items-center gap-4 p-4 md:grid-cols-[1fr_1.1fr] md:gap-5 md:p-5 lg:gap-6 lg:p-6">
            <div className="overflow-hidden rounded-xl">
              <img src={heroImg} alt="Healic+ Membership Card — savings for your family" className="h-full w-full object-contain md:max-h-[260px]" loading="eager" />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 rounded-pill bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-accent">
                Exclusive Benefits
              </span>
              <h1 className="mt-2 font-display text-xl font-bold leading-tight text-foreground sm:text-2xl lg:text-3xl">
                Healic<span className="text-accent">+</span> Membership Card
              </h1>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                Huge savings on medical bills for you & your family — pick a plan that fits and enjoy benefits across every Healic hospital.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta transition hover:opacity-90">
                  Get Your Card
                </Link>
                <a href="#packages" className="inline-flex items-center rounded-xl border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted">
                  Compare Plans
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perks strip */}
      <section className="mx-auto max-w-7xl px-4 pb-4 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {perks.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-5 shadow-card">
              <p.icon className="h-6 w-6 text-accent" />
              <h3 className="mt-3 font-display text-base font-semibold text-foreground">{p.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <header className="mb-10 text-center">
          <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Choose Your Plan</h2>
          <p className="mt-2 text-muted-foreground">Three flexible tiers — Heal, Heal+ and Heal++.</p>
        </header>

        <div className="grid gap-6 lg:grid-cols-3">
          {packages.map((pkg) => {
            const Icon = pkg.icon;
            return (
              <div
                key={pkg.id}
                className={`relative flex flex-col rounded-3xl border bg-card p-7 shadow-card transition hover:shadow-lg ${
                  pkg.highlight
                    ? "border-accent ring-2 ring-accent/40"
                    : "border-border"
                }`}
              >
                {pkg.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-pill bg-accent px-4 py-1 text-xs font-semibold uppercase tracking-wide text-accent-foreground">
                    Most Popular
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <span className="rounded-xl bg-primary-soft p-2 text-primary">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-foreground">{pkg.name}</h3>
                    <p className="text-xs text-muted-foreground">{pkg.tagline}</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3 rounded-2xl bg-muted p-4 text-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Pay</p>
                    <p className="mt-1 font-display text-lg font-bold text-foreground">{pkg.pay}</p>
                  </div>
                  <div className="border-x border-border">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Get</p>
                    <p className="mt-1 font-display text-lg font-bold text-foreground">{pkg.get}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Instant</p>
                    <p className="mt-1 font-display text-sm font-bold text-accent">{pkg.instant}</p>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 text-sm">
                  {pkg.benefits.map((b) => (
                    <li key={b.label} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>
                        <span className="font-semibold text-foreground">{b.label}:</span>{" "}
                        <span className="text-muted-foreground">{b.value}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`mt-8 inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition ${
                    pkg.highlight
                      ? "bg-accent text-accent-foreground hover:opacity-90"
                      : "bg-primary text-primary-foreground hover:opacity-90"
                  }`}
                >
                  Choose {pkg.name}
                </Link>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          * Benefits valid for 12 months across all Healic hospitals. Diagnostics discount not applicable on packages.
        </p>
      </section>

      <BookingForm title="Get Your Healic+ Membership Card" mascotSrc={membershipMascot.url} />
    </PageShell>
  );
}
